import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { faArrowUp } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { AI_TEXT_ACTIONS } from '@/components/shared/ai-menu-constants';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Icon } from '@/components/ui/icon';
import { MutedText } from '@/components/ui/typography';
import { TaskType } from '@/types/assistant';

interface PromptProps {
  onSubmit: (prompt: string) => void;
  onQuickAction: (task: TaskType, title: string) => void;
}

interface QuickActionItemProps {
  action: (typeof AI_TEXT_ACTIONS)[number];
  onSelect: (task: TaskType) => void;
}

function QuickActionItem({ action, onSelect }: QuickActionItemProps) {
  return (
    <CommandItem key={action.key} value={action.label} onSelect={() => onSelect(action.task)}>
      <Icon icon={action.icon} className="text-primary" />
      {action.label}
    </CommandItem>
  );
}

export function Prompt({ onSubmit, onQuickAction }: PromptProps) {
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const hasPrompt = useMemo(() => inputValue.trim().length > 0, [inputValue]);

  const handleSelect = useCallback(
    (task: TaskType) => {
      if (task === TaskType.EXTEND) {
        onQuickAction(task, 'Continue writing');
        return;
      }

      const action = AI_TEXT_ACTIONS.find((a) => a.task === task);
      if (action) {
        onQuickAction(task, action.label);
      }
    },
    [onQuickAction],
  );

  const handleSubmit = useCallback(() => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      onSubmit(trimmed);
      setInputValue('');
      setSelectedValue('');
    }
  }, [onSubmit, inputValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        // If there is a selected command item, let cmdk handle Enter so it
        // triggers that item's onSelect instead of submitting a custom prompt.
        if (selectedValue) {
          return;
        }

        e.preventDefault();
        const trimmed = inputValue.trim();
        if (trimmed) {
          handleSubmit();
        }
      }
    },
    [inputValue, selectedValue, handleSubmit],
  );

  const filterFunction = useCallback((value: string, search: string) => {
    if (!search) return 1;
    const normalizedValue = value.toLowerCase();
    const normalizedSearch = search.toLowerCase();
    // Exact match gets highest priority
    if (normalizedValue === normalizedSearch) return 1;
    // Starts with search gets high priority
    if (normalizedValue.startsWith(normalizedSearch)) return 1;
    // Contains search gets lower priority
    if (normalizedValue.includes(normalizedSearch)) return 1;
    return 0;
  }, []);

  // Focus input when component becomes visible (e.g., after cancel)
  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frameId);
  }, []);

  return (
    <Command
      filter={filterFunction}
      className="rounded-lg border-none"
      value={selectedValue}
      onValueChange={setSelectedValue}
      onKeyDown={handleKeyDown}
    >
      <div className="relative flex items-center">
        <CommandInput
          ref={inputRef}
          placeholder="Ask AI to edit or generate..."
          aria-label="AI prompt input"
          className="pr-10"
          containerClassName="flex-1"
          value={inputValue}
          onValueChange={setInputValue}
        />
        <Button
          size="icon-sm"
          disabled={!hasPrompt}
          onClick={handleSubmit}
          aria-label="Submit prompt"
        >
          <Icon icon={faArrowUp} />
        </Button>
      </div>

      <CommandList>
        <CommandGroup heading="Edit or review selection">
          {AI_TEXT_ACTIONS.filter((action) => action.key !== 'continue-writing').map((action) => (
            <QuickActionItem key={action.key} action={action} onSelect={handleSelect} />
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Use AI to do more">
          {AI_TEXT_ACTIONS.filter((action) => action.key === 'continue-writing').map((action) => (
            <QuickActionItem key={action.key} action={action} onSelect={handleSelect} />
          ))}
        </CommandGroup>

        <CommandEmpty>
          <MutedText>Press Enter to submit custom prompt</MutedText>
        </CommandEmpty>
      </CommandList>
    </Command>
  );
}
